(function() {
    if (customElements.get('base-tabs-nav') === undefined) {
        const template = document.createElement('template');
        template.innerHTML = `
        <style>
        :host {
            --base-tabs--tabs-background: transparent;
            --base-tabs--tab-separator-color: #ccc;
            --base-tabs--tab-background: #ffffff;
            --base-tabs--tab-hover-background: #dfdfdf;
            --base-tabs--tab-active-background: #d6d6d6;
            --base-tabs--tab-color: #000000;
        
            display: block;
            position: relative;
            background-color: var(--base-tabs--tabs-background);
        }
        
        :host([flow*="vertical"]) [part="tabs-nav"] {
            flex-direction: column;
        }
        
        [part="tabs-wrapper"] {
            overflow-x: auto;
            position: relative;
        }
        
        [part="tabs-nav"] {
            display: inline-flex;
            border: solid 1px var(--base-tabs--tab-separator-color);
        }
        
        /* @todo dont appply so much style on slotted elements */
        [part="tabs-nav"] ::slotted(*) {
            padding: 20px;
            cursor: pointer;
            color: var(--base-tabs--tab-color);
            background-color: var(--base-tabs--tab-background);
        }
        
        [part="tabs-nav"] ::slotted(a) {
            text-decoration: none;
        }
        
        [part="tabs-nav"] ::slotted(button) {
            appearance: none;
            border: none;
        }
        
        :host(:is(:not([flow]), [flow*="horizontal"])) [part="tabs-nav"] ::slotted(*:not(:last-child)) {
            border-right: solid 1px var(--base-tabs--tab-separator-color);
        }
        
        :host(:is([flow*="vertical"])) [part="tabs-nav"] ::slotted(*:not(:last-child)) {
            border-bottom: solid 1px var(--base-tabs--tab-separator-color);
        }
        
        [part="tabs-nav"] ::slotted(*:hover) {
            background-color: var(--base-tabs--tab-hover-background);
        }
        
        [part="tabs-nav"] ::slotted(*[aria-selected="true"]) {
            background-color: var(--base-tabs--tab-active-background);
        }
        
        [part^="default-arrow"] {
            width: 7px;
            height: 7px;
            box-sizing: border-box;
            position: absolute;
        }
        
        [part^="default-arrow"]::before {
            content: '';
            width: 100%;
            height: 100%;
            border-width: .8vmin .8vmin 0 0;
            border-style: solid;
            border-color: black;
            transition: .2s ease;
            display: block;
            transform-origin: 100% 0;
        }
        
        :host(:is(:not([flow]), [flow*="horizontal"])) [part="default-arrow-start"] {
            transform: rotate(-130deg) translateX(-2px);
        }
        
        :host(:is(:not([flow]), [flow*="horizontal"])) [part="default-arrow-end"] {
            transform: rotate(45deg) translateX(-2px);
        }
        
        [part^="arrow-scroll"] {
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.8);
            position: absolute;
            z-index: 1;
            width: 20px;
        }
        
        [part^="arrow-scroll"][hidden] {
            display: none;
        }
        
        :host(:is(:not([flow]), [flow*="horizontal"])) [part="arrow-scroll-start"] {
            inset: 0 auto 0 0;
        }
        
        :host(:is(:not([flow]), [flow*="horizontal"])) [part="arrow-scroll-end"] {
            inset: 0 0 0 auto;
        }
        </style>
        <div part="arrow-scroll-start" hidden="hidden">
            <slot name="arrow-scroll-start">
                <div part="default-arrow-start"></div>
            </slot>
        </div>
        <div part="tabs-wrapper">
            <nav part="tabs-nav">
                <slot></slot>
            </nav>
        </div>
        <div part="arrow-scroll-end" hidden="hidden">
            <slot name="arrow-scroll-end">
                <div part="default-arrow-end"></div>
            </slot>
        </div>
        `;
        customElements.define('base-tabs-nav', class extends HTMLElement {
            constructor() {
                super();
                this.attachShadow({mode: "open"}).appendChild(template.content.cloneNode(true));
            }

            get isVertical() {
                const flow = this.getAttribute('flow');
                return flow !== null && flow.indexOf('vertical') > -1;
            }

            get arrowStart() {
                return this.shadowRoot.querySelector('[part="arrow-scroll-start"]');
            }

            get arrowEnd() {
                return this.shadowRoot.querySelector('[part="arrow-scroll-end"]');
            }

            get tabs() {
                return Array.from(this.querySelectorAll(':scope > *:not([slot])'));
            }

            get useHash() {
                const value = this.getAttribute('use-hash');
                return !(value === null || value === '0' || value.toLowerCase() === 'false');
            }

            connectedCallback() {
                this.shadowRoot.querySelector('[part="tabs-nav"]').addEventListener('click', (event) => {
                    const control = event.target.closest('[aria-controls]');
                    if (control) {
                        event.preventDefault();
                        const tabChange = new CustomEvent('tabchange', {
                            detail: {
                                control: control,
                                originalEvent: event,
                                cancelable: true,
                            }
                        });
                        this.dispatchEvent(tabChange);
                        if (!tabChange.defaultPrevented) {
                            if (this.useHash) {
                                window.location.hash = control.getAttribute('aria-controls');
                            }
                            this.tabs.forEach(tab => {
                                tab.setAttribute('aria-selected', tab === control ? 'true' : 'false');
                            });
                            const target = document.getElementById(control.getAttribute('aria-controls'));
                            if (target && target.parentElement) {
                                const panes = Array.from(target.parentElement.querySelectorAll('[role="tabpanel"]'));
                                panes.forEach(pane => {
                                    if (pane === target) {
                                        if (this.getAttribute('pane-state') === 'aria') {
                                            pane.setAttribute('aria-expanded', 'true');
                                        } else {
                                            pane.removeAttribute('hidden');
                                        }
                                    } else {
                                        if (this.getAttribute('pane-state') === 'aria') {
                                            pane.setAttribute('aria-expanded', 'false');
                                        } else {
                                            pane.setAttribute('hidden', 'hidden');
                                        }
                                    }
                                });
                            }
                        }
                    }
                }, {
                    capture: true,
                });

                if (this.useHash && window.location.hash) {
                    const setStateFromHash = () => {
                        const possibleTab = this.tabs.find(tab => tab.getAttribute('aria-controls') === window.location.hash.substr(1));
                        if (possibleTab) {
                            possibleTab.dispatchEvent(new Event('click'));
                        }
                    };
                    if (document.readyState !== "loading") {
                        setStateFromHash();
                    } else {
                        document.addEventListener('DOMContentLoaded', setStateFromHash, {once: true});
                    }
                }

                const tabsWrapper = this.shadowRoot.querySelector('[part="tabs-wrapper"]');
                let timeout;
                const toggleArrows = () => {
                    clearTimeout(timeout);
                    timeout = setTimeout(() => {
                        if (this.isVertical) {
                            // @todo add support for vertical scrolling
                            this.arrowStart.setAttribute('hidden', 'hidden');
                            this.arrowEnd.setAttribute('hidden', 'hidden');
                            return;
                        }
                        if (tabsWrapper.scrollLeft > 0) {
                            this.arrowStart.removeAttribute('hidden');
                        } else {
                            this.arrowStart.setAttribute('hidden', 'hidden');
                        }
                        if (tabsWrapper.scrollWidth > tabsWrapper.clientWidth + tabsWrapper.scrollLeft) {
                            this.arrowEnd.removeAttribute('hidden');
                        } else {
                            this.arrowEnd.setAttribute('hidden', 'hidden');
                        }
                    }, 20);
                };

                toggleArrows();
                new ResizeObserver(toggleArrows).observe(this.parentElement);
                tabsWrapper.addEventListener('scroll', toggleArrows, {passive: true});

                this.arrowEnd.addEventListener("click", () => {
                    tabsWrapper.scroll({left: tabsWrapper.scrollLeft + tabsWrapper.clientWidth / 2, behavior: 'smooth'});
                });
                this.arrowStart.addEventListener("click", () => {
                    tabsWrapper.scroll({left: tabsWrapper.scrollLeft - tabsWrapper.clientWidth / 2, behavior: 'smooth'});
                });
            }
        });
    }
})();
